import React, { useState, useRef, KeyboardEvent } from "react";
import { ModalKind } from "./types";

type Props = {
  onSend: (text: string) => void;
  onOpenModal: (modal: ModalKind) => void;
  disabled?: boolean;
  placeholder?: string;
};

export const ChatInput: React.FC<Props> = ({
  onSend,
  onOpenModal,
  disabled = false,
  placeholder = "Message your play…",
}) => {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function autoResize() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setValue(e.target.value);
    autoResize();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  function submit() {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }

  return (
    <div className="flex items-end gap-2 bg-white border border-black/10 rounded-2xl px-3 py-2.5 shadow-sm focus-within:border-brand-accent/40 focus-within:shadow-md transition-all">
      {/* Attach button — opens add-proof modal */}
      <button
        type="button"
        onClick={() => onOpenModal("add-proof")}
        className="flex-none w-8 h-8 rounded-xl flex items-center justify-center text-brand-muted hover:text-brand-ink hover:bg-black/5 transition-all mb-0.5"
        title="Add proof"
        aria-label="Add proof document"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M9 3v12M3 9h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>

      {/* Auto-resizing textarea */}
      <textarea
        ref={textareaRef}
        className="flex-1 bg-transparent text-sm text-brand-ink placeholder:text-brand-muted resize-none outline-none leading-relaxed py-1 min-h-[24px] max-h-[200px]"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        rows={1}
      />

      {/* Action menu — quick access to all modals */}
      <div className="flex items-end gap-1 mb-0.5">
        <button
          type="button"
          onClick={() => onOpenModal("edit-profile")}
          className="w-8 h-8 rounded-xl flex items-center justify-center text-brand-muted hover:text-brand-ink hover:bg-black/5 transition-all"
          title="Edit profile"
          aria-label="Edit your profile"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.4" />
            <path
              d="M2.5 13.5c0-3 2.5-4.5 5.5-4.5s5.5 1.5 5.5 4.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => onOpenModal("compose-dm")}
          className="w-8 h-8 rounded-xl flex items-center justify-center text-brand-muted hover:text-brand-ink hover:bg-black/5 transition-all"
          title="Compose message"
          aria-label="Compose a DM or email"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect
              x="1.5"
              y="3.5"
              width="13"
              height="9"
              rx="1.5"
              stroke="currentColor"
              strokeWidth="1.4"
            />
            <path
              d="M1.5 5l6.5 4 6.5-4"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Send button */}
        <button
          type="button"
          onClick={submit}
          disabled={!value.trim() || disabled}
          className="w-8 h-8 rounded-xl flex items-center justify-center bg-brand-accent text-white hover:bg-brand-accent-strong transition-all disabled:opacity-30 disabled:cursor-not-allowed active:scale-95"
          aria-label="Send message"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M13 8L3 3l2 5-2 5 10-5z" fill="currentColor" />
          </svg>
        </button>
      </div>
    </div>
  );
};
